import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { toast } from 'sonner';
import { AuthProvider } from './contexts/AuthContext';
import { ActiveVisitProvider } from './contexts/ActiveVisitContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { RoleGuard } from './components/auth';
import { Layout } from './components/layout/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Leaderboard from './pages/Leaderboard';
import AgentPerformance from './pages/AgentPerformance';
import Customers from './pages/Customers';
import { CustomerForm } from './components/customers/CustomerForm';
import Visits from './pages/Visits';
import Todos from './pages/Todos';
import Reports from './pages/Reports';
import { VisitWizard } from './components/visits/VisitWizard';
import DeliveryBatches from './pages/DeliveryBatches';
import Products from './pages/Products';
import Manufacturers from './pages/Manufacturers';
import Distributors from './pages/Distributors';
import Orders from './pages/Orders';
import RoutesPage from './pages/Routes';
import PoSDetail from './pages/PoSDetail';
import DistributorOrderReport from './pages/DistributorOrderReport';
import CompanySettings from './pages/TeamSettings';
import Lieferscheine from './pages/Lieferscheine';
import { syncPendingSubmissions } from './services/syncService';

const Onboardings = lazy(() => import('./pages/Onboardings'));
const PublicOnboarding = lazy(() => import('./pages/PublicOnboarding'));

// Admin pages (Super Admin only)
import { AdminTeams, CreateTeam, TeamDetail } from './pages/admin';

// Team management pages (Team Admin + Super Admin)
import { TeamSettings, TeamUsers } from './pages/team';

function App() {
  // Sync pending submissions on mount if online
  useEffect(() => {
    const syncOnMount = async () => {
      if (navigator.onLine) {
        // Wait a bit for auth to complete
        setTimeout(async () => {
          try {
            const result = await syncPendingSubmissions();
            
            if (result.synced > 0) {
              toast.success(`Synced ${result.synced} offline submissions`);
            }
            
            if (result.failed > 0) {
              toast.error(`Failed to sync ${result.failed} submissions`);
              // Log errors for debugging
              result.errors.forEach(err => console.error('[Sync Error]', err));
            }
          } catch (error) {
            console.error('[App] Mount sync failed:', error);
          }
        }, 2000);
      }
    };

    syncOnMount();
  }, []);

  // Listen for online event and sync pending submissions
  useEffect(() => {
    const handleOnline = async () => {
      console.log('[App] Connection restored, syncing pending submissions...');
      
      // Dispatch sync start event
      window.dispatchEvent(new Event('sync-start'));
      
      try {
        const result = await syncPendingSubmissions();
        
        // Dispatch sync complete event
        window.dispatchEvent(new Event('sync-complete'));
        
        if (result.synced > 0) {
          toast.success(`Successfully synced ${result.synced} offline submissions`);
        }
        
        if (result.failed > 0) {
          toast.error(`Failed to sync ${result.failed} submissions. They will retry later.`);
          // Log errors for debugging
          result.errors.forEach(err => console.error('[Sync Error]', err));
        }
      } catch (error) {
        console.error('[App] Sync failed:', error);
        window.dispatchEvent(new Event('sync-complete'));
      }
    };

    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  return (
    <AuthProvider>
      <ActiveVisitProvider>
      <div className="min-h-screen bg-gray-50 text-gray-900 font-sans">
        <Routes>
          <Route path="/login" element={<Login />} />

          {/* Public onboarding wizard (token-gated, no JWT) */}
          <Route
            path="/onboarding/:token"
            element={
              <Suspense
                fallback={
                  <div className="text-center py-12 text-gray-500 text-sm">Laden...</div>
                }
              >
                <PublicOnboarding />
              </Suspense>
            }
          />

          <Route element={<ProtectedRoute />}>
            <Route path="/" element={<Layout><Navigate to="/dashboard" replace /></Layout>} />
            <Route path="/dashboard" element={<Layout><Dashboard /></Layout>} />
            <Route path="/profile" element={<Layout><Profile /></Layout>} />
            <Route path="/leaderboard" element={<Layout><Leaderboard /></Layout>} />
            <Route path="/leaderboard/:userId" element={<Layout><AgentPerformance /></Layout>} />
            <Route path="/pos" element={<Layout><Customers /></Layout>} />
            <Route path="/pos/new" element={<Layout><CustomerForm /></Layout>} />
            <Route path="/pos/:id" element={<Layout><PoSDetail /></Layout>} />
            <Route path="/pos/:id/edit" element={<Layout><CustomerForm /></Layout>} />
            <Route path="/visits" element={<Layout><Visits /></Layout>} />
            <Route path="/todos" element={<Layout><Todos /></Layout>} />
            <Route path="/routes" element={<Layout><RoutesPage /></Layout>} />
            
            {/* Visit Wizard */}
            <Route path="/visits/new" element={<Layout><VisitWizard /></Layout>} />
            
            <Route path="/delivery-batches" element={<Layout><DeliveryBatches /></Layout>} />
            <Route path="/manufacturers" element={<Layout><Manufacturers /></Layout>} />
            <Route path="/products" element={<Layout><Products /></Layout>} />
            <Route path="/orders" element={<Layout><Orders /></Layout>} />
            <Route path="/distributors" element={<Layout><Distributors /></Layout>} />
            <Route
              path="/onboardings"
              element={
                <Layout>
                  <Suspense
                    fallback={
                      <div className="text-center py-12 text-gray-500 text-sm">Laden...</div>
                    }
                  >
                    <Onboardings />
                  </Suspense>
                </Layout>
              }
            />
            <Route path="/reports" element={<Layout><Reports /></Layout>} />
            <Route path="/reports/distributor-orders" element={<Layout><DistributorOrderReport /></Layout>} />
            <Route path="/settings" element={<Layout><CompanySettings /></Layout>} />
            <Route path="/lieferscheine" element={<Layout><Lieferscheine /></Layout>} />
            <Route
              path="/bestellungen"
              element={<Navigate to="/orders?tab=distributors" replace />}
            />
            
            {/* Admin Routes - Super Admin Only */}
            <Route element={<RoleGuard roles={['super_admin']} redirectTo="/dashboard" />}>
              <Route path="/admin/teams" element={<Layout><AdminTeams /></Layout>} />
              <Route path="/admin/teams/new" element={<Layout><CreateTeam /></Layout>} />
              <Route path="/admin/teams/:teamId" element={<Layout><TeamDetail /></Layout>} />
            </Route>
            
            {/* Team Management Routes - Team Admin + Super Admin */}
            <Route element={<RoleGuard roles={['super_admin', 'team_admin']} redirectTo="/dashboard" />}>
              <Route path="/team/settings" element={<Layout><TeamSettings /></Layout>} />
              <Route path="/team/users" element={<Layout><TeamUsers /></Layout>} />
            </Route>

          </Route>
        </Routes>
      </div>
      </ActiveVisitProvider>
    </AuthProvider>
  );
}

export default App;
